import React, { useState, useEffect } from 'react';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import LandingPage from './pages/LandingPage';
import HomePage from './pages/HomePage';
import MenuPage from './pages/MenuPage';
import ProductDetailPage from './pages/ProductDetailPage';
import StoreSelectionPage from './pages/StoreSelectionPage';
import CheckoutPage from './pages/CheckoutPage'; // ← AÑADIDO
import CartPage from './pages/CartPage';
import ProfilePage from './pages/ProfilePage';
import StorePage from './pages/StorePage';
import CardsPage from './pages/CardsPage';
import OrdersPage from './pages/OrdersPage';
import TimePage from './pages/TimePage';
import BottomNav from './components/Navigation/BottomNav';

import TopBar from './components/Navigation/TopBar';
import Login from './components/Auth/Login';
import SignUp from './components/Auth/SignUp';
import PhoneVerification from './components/Auth/PhoneVerification';
import VerificationSuccess from './components/Auth/VerificationSuccess';
import { TiArrowBack } from "react-icons/ti";
import './App.css';

function App() {
  // ========== ESTADOS PRINCIPALES ==========
  const [showLanding, setShowLanding] = useState(true);
  const [authStep, setAuthStep] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentPage, setCurrentPage] = useState('home');

  // Estados para menú y productos
  const [menuCategory, setMenuCategory] = useState('coffee');
  const [showProductDetail, setShowProductDetail] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Estados para takeaway y selección de tienda
  const [showStoreSelection, setShowStoreSelection] = useState(false);
  const [pendingProduct, setPendingProduct] = useState(null);
  const [productOptions, setProductOptions] = useState({
    size: 'small',
    sweetness: 'normal',
    takeaway: false,
    quantity: 1,
    selectedStore: null
  });

  // Estado para orden actual
  // Estado para orden actual
  const [currentOrder, setCurrentOrder] = useState(null);
  const [showOrderConfirmation, setShowOrderConfirmation] = useState(false);
  const [autoAdd, setAutoAdd] = useState(false); // New state for auto-add flow

  // ========== EFFECTS ==========
  useEffect(() => {
    // Siempre empezar desde el landing page
    localStorage.removeItem('cafeMoctezumaUser'); // Limpiar sesiones anteriores
    setIsLoggedIn(false);
    setShowLanding(true);
    setAuthStep(null);
    setCurrentPage('home');
    resetAllStates();
  }, []); // Solo se ejecuta una vez al montar

  // ========== FUNCIONES DE MANEJO ==========

  // Landing Page
  const handleEnterApp = () => {
    setShowLanding(false);
    setAuthStep('login');
  };

  // Autenticación
  const handleLoginSuccess = (userData) => {
    setIsLoggedIn(true);
    setAuthStep(null);
    localStorage.setItem('cafeMoctezumaUser', JSON.stringify(userData));
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setAuthStep('login');
    localStorage.removeItem('cafeMoctezumaUser');
    setCurrentPage('home');
    resetAllStates();
  };

  const handleSignUpSuccess = () => {
    setAuthStep('phone');
  };

  const handleVerificationSuccess = () => {
    setAuthStep('verified');
  };

  // Resetear todos los estados
  const resetAllStates = () => {
    setShowProductDetail(false);
    setSelectedProduct(null);
    setShowStoreSelection(false);
    setPendingProduct(null);
    setProductOptions({
      size: 'small',
      sweetness: 'normal',
      takeaway: false,
      quantity: 1,
      selectedStore: null
    });
    setCurrentOrder(null);
    setShowOrderConfirmation(false);
    setAutoAdd(false);
  };

  // Navegación a menú
  const handleNavigateToMenu = (category) => {
    setMenuCategory(category);
    setCurrentPage('menu');
    resetProductStates();
  };

  const resetProductStates = () => {
    setShowProductDetail(false);
    setSelectedProduct(null);
    setShowStoreSelection(false);
    setPendingProduct(null);
    setAutoAdd(false);
  };

  // Manejo de productos
  const handleProductSelect = (product) => {
    setSelectedProduct({
      ...product,
      category: menuCategory
    });
    setShowProductDetail(true);
    setShowStoreSelection(false);
  };

  const handleBackFromDetail = () => {
    setShowProductDetail(false);
    setSelectedProduct(null);
    setCurrentPage('menu');
  };

  const handleMakeAnother = () => {
    setShowProductDetail(false);
    setSelectedProduct(null);
    setCurrentPage('menu');
  };

  // Manejo de takeaway y selección de tienda
  const handleTakeawaySelect = (options) => {
    // Guardar las opciones del producto
    setProductOptions(options);

    // Guardar el producto pendiente
    setPendingProduct(selectedProduct);

    // Mostrar selección de tienda
    setShowProductDetail(false);
    setShowStoreSelection(true);
  };

  const handleStoreSelected = (store) => {
    // Actualizar opciones con la tienda seleccionada
    setProductOptions(prev => ({
      ...prev,
      takeaway: true,
      selectedStore: store
    }));

    // Volver al producto con takeaway habilitado
    setShowStoreSelection(false);
    setShowProductDetail(true);
    setAutoAdd(true); // Enable auto-add
  };

  const handleBackFromStoreSelection = () => {
    // Volver al detalle del producto
    setShowStoreSelection(false);
    setShowProductDetail(true);
  };

  // Manejo del carrito y checkout
  const handleNavigateToCart = () => {
    setCurrentPage('cart');
    resetProductStates();
  };

  const handleNavigateToCheckout = () => {
    setCurrentPage('checkout');
    resetProductStates();
  };

  const handleTrackOrder = () => {
    // Si hay items en el carrito, ir al checkout
    setCurrentPage('checkout');
    resetProductStates();
  };

  const handleOrderPlaced = (orderData) => {
    // Guardar la orden actual
    setCurrentOrder(orderData);
    setShowOrderConfirmation(true);
    setCurrentPage('home'); // Volver a home para mostrar la orden lista
  };

  const handleBackToHomeFromConfirmation = () => {
    setShowOrderConfirmation(false);
    setCurrentOrder(null);
    setCurrentPage('home');
  };

  // ========== RENDERIZADO DE PÁGINAS ==========

  const renderPage = () => {
    // 1. Landing Page
    if (showLanding) {
      return <LandingPage onEnterApp={handleEnterApp} />;
    }

    // 2. Flujo de autenticación (si no está logueado)
    if (!isLoggedIn) {
      switch (authStep) {
        case 'login':
          return (
            <Login
              onLoginSuccess={handleLoginSuccess}
              onNavigateToSignUp={() => setAuthStep('signup')}
              onSkipLogin={() => {
                setIsLoggedIn(true);
                setAuthStep(null);
              }}
            />
          );
        case 'signup':
          return (
            <SignUp
              onSignUpSuccess={handleSignUpSuccess}
              onNavigateToLogin={() => setAuthStep('login')}
            />
          );
        case 'phone':
          return (
            <PhoneVerification
              onVerificationSuccess={handleVerificationSuccess}
              onBack={() => setAuthStep('signup')}
            />
          );
        case 'verified':
          return (
            <VerificationSuccess
              onComplete={() => {
                setIsLoggedIn(true);
                setAuthStep(null);
              }}
            />
          );
        default:
          return <Login
            onLoginSuccess={handleLoginSuccess}
            onNavigateToSignUp={() => setAuthStep('signup')}
          />;
      }
    }

    // 3. Página de selección de tienda para takeaway
    if (showStoreSelection) {
      return (
        <StoreSelectionPage
          onStoreSelected={handleStoreSelected}
          onBack={handleBackFromStoreSelection}
        />
      );
    }

    // 4. Página de detalle del producto
    if (showProductDetail && selectedProduct) {
      return (
        <ProductDetailPage
          product={selectedProduct}
          initialOptions={productOptions}
          onBack={handleBackFromDetail}
          onMakeAnother={handleMakeAnother}
          onSelectStore={handleTakeawaySelect}
          autoAdd={autoAdd}
          onCheckout={handleNavigateToCheckout}
        />
      );
    }

    // 5. Página de confirmación de orden (después del pago)
    if (showOrderConfirmation && currentOrder) {
      return (
        <div className="order-confirmation-page">
          <div className="confirmation-content">
            <div className="success-icon">✅</div>
            <h2>Su pago fue exitoso!</h2>
            <p className="order-number">Pedido #{currentOrder.id}</p>

            <div className="qr-code">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=ORDER-${currentOrder.id}`}
                alt={`QR Code for order ${currentOrder.id}`}
                style={{ width: '150px', height: '150px', display: 'block', margin: '0 auto' }}
              />
            </div>

            <p className="qr-instruction">Enseña el QR en nuestro mostrador para recoger tu pedido</p>

            <div className="pickup-info">
              <h3>Ubicación de Pickup</h3>
              <p>{currentOrder.store?.name || 'Selected Store'}</p>
              <p>{currentOrder.store?.address || 'Store Address'}</p>
              <p className="ready-time">Listo en 10-15 minutos</p>
            </div>

            <div className="confirmation-actions">
              <button
                className="action-btn secondary"
                onClick={() => {
                  // Función para compartir o guardar
                  alert('Order details saved!');
                }}
              >
                Guardar recibo
              </button>
              <button
                className="action-btn primary"
                onClick={handleBackToHomeFromConfirmation}
              >
                <TiArrowBack />
              </button>
            </div>
          </div>
        </div>
      );
    }

    // 6. Páginas principales de la app
    switch (currentPage) {
      case 'home':
        return (
          <HomePage
            onNavigateToMenu={handleNavigateToMenu}
            onTrackOrder={handleTrackOrder}
            currentOrder={currentOrder}
          />
        );
      case 'menu':
        return (
          <MenuPage
            category={menuCategory}
            onBack={() => setCurrentPage('home')}
            onProductSelect={handleProductSelect}
          />
        );
      case 'cart':
        return (
          <CartPage
            setCurrentPage={setCurrentPage}
            onCheckout={handleNavigateToCheckout}
          />
        );
      case 'checkout':
        return (
          <CheckoutPage
            setCurrentPage={setCurrentPage}
            onOrderPlaced={handleOrderPlaced}
          />
        );
      case 'store':
        return <StorePage />;
      case 'cards':
        return <CardsPage />;
      case 'order':
        return <OrdersPage />;
      case 'time': // Add time case
        return <TimePage />;
      case 'profile':
        return <ProfilePage onLogout={handleLogout} />;
      default:
        return (
          <HomePage
            onNavigateToMenu={handleNavigateToMenu}
            onTrackOrder={handleTrackOrder}
            currentOrder={currentOrder}
          />
        );
    }
  };

  // ========== DETERMINAR SI MOSTRAR TOPBAR ==========

  const shouldShowTopBar = () => {
    return isLoggedIn &&
      !showLanding &&
      !authStep &&
      !showStoreSelection &&
      !showOrderConfirmation &&
      (currentPage !== 'home' || showProductDetail);
  };

  const getTopBarTitle = () => {
    if (showProductDetail) return 'Customize Order';
    if (showStoreSelection) return 'Select Store';
    if (currentPage === 'menu') return 'Menu';
    if (currentPage === 'cart') return 'Cart';
    if (currentPage === 'checkout') return 'Checkout';
    if (currentPage === 'store') return 'Stores';
    if (currentPage === 'cards') return 'Tarjetas';
    if (currentPage === 'order') return 'Pedidos';
    if (currentPage === 'time') return 'Tiempo'; // Add time title
    if (currentPage === 'profile') return 'Profile';
    return 'Cafe Motezuma';
  };

  const getTopBarBackAction = () => {
    if (showProductDetail) return handleBackFromDetail;
    if (showStoreSelection) return handleBackFromStoreSelection;
    if (currentPage === 'menu') return () => setCurrentPage('home');
    if (currentPage === 'cards') return () => setCurrentPage('home');
    if (currentPage === 'order') return () => setCurrentPage('home');
    if (currentPage === 'time') return () => setCurrentPage('home'); // Add time back action
    if (currentPage === 'cart') return () => setCurrentPage('home');
    if (currentPage === 'checkout') return () => setCurrentPage('cart');
    return null;
  };

  const shouldShowBackButton = () => {
    return showProductDetail || showStoreSelection ||
      currentPage === 'menu' ||
      currentPage === 'cart' ||
      currentPage === 'checkout';
  };

  // ========== DETERMINAR SI MOSTRAR BOTTOM NAV ==========

  const shouldShowBottomNav = () => {
    return isLoggedIn &&
      !showLanding &&
      !authStep &&
      !showProductDetail &&
      !showStoreSelection &&
      !showOrderConfirmation &&
      currentPage !== 'checkout' &&
      currentPage !== 'cart';
  };

  // ========== RENDER PRINCIPAL ==========

  return (
    <AuthProvider>
      <CartProvider>
        <div className="app">
          {/* TopBar */}
          {shouldShowTopBar() && (
            <TopBar
              currentPage={currentPage}
              isLoggedIn={isLoggedIn}
              onLogout={handleLogout}
              showBackButton={shouldShowBackButton()}
              onBack={getTopBarBackAction()}
              title={getTopBarTitle()}
            />
          )}

          {/* Contenido principal */}
          <main className="main-content">
            {renderPage()}
          </main>

          {/* BottomNav */}
          {shouldShowBottomNav() && (
            <BottomNav
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
            />
          )}
        </div>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;